import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Shield, BarChart3, Megaphone, Puzzle, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Switch as SwitchBase } from '../components/ui/switch';

// Switch comes from a .jsx file; cast to any so TS accepts forwarded props like id/aria-label.
const Switch = SwitchBase as unknown as React.ComponentType<any>;

export type CookiePreferences = {
  necessary: true;
  analytics: boolean;
  marketing: boolean;
  functional: boolean;
};

export type CookiePreferencesModalProps = {
  open: boolean;
  initial: CookiePreferences;
  onClose: () => void;
  onSave: (next: CookiePreferences) => void;
  onAcceptAll?: () => void;
  onRejectAll?: () => void;
};

function getFocusableElements(container: HTMLElement): HTMLElement[] {
  const selector =
    'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';
  return Array.from(container.querySelectorAll(selector)) as HTMLElement[];
}

type CategoryRow = {
  key: 'necessary' | 'analytics' | 'marketing' | 'functional';
  title: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  locked?: boolean;
};

const CATEGORIES: CategoryRow[] = [
  {
    key: 'necessary',
    title: 'Necessari',
    description: 'Autenticazione, sicurezza, funzionamento base. Sempre attivi.',
    icon: Shield,
    locked: true,
  },
  {
    key: 'analytics',
    title: 'Analitici / Statistiche',
    description: 'Misurazione utilizzo e miglioramento del prodotto (es. PostHog, telemetria).',
    icon: BarChart3,
  },
  {
    key: 'marketing',
    title: 'Marketing',
    description: 'Pixel/retargeting e pubblicità (se presenti).',
    icon: Megaphone,
  },
  {
    key: 'functional',
    title: 'Funzionali',
    description: 'Servizi esterni opzionali (es. mappe, chat di supporto).',
    icon: Puzzle,
  },
];

export default function CookiePreferencesModal(props: CookiePreferencesModalProps) {
  const { open, initial, onClose, onSave, onAcceptAll, onRejectAll } = props;

  const [state, setState] = useState<CookiePreferences>(initial);

  const dialogRef = useRef<HTMLDivElement | null>(null);
  const lastActiveRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!open) return;
    setState(initial);
  }, [open, initial]);

  const titleId = useMemo(
    () => `sp-cookie-modal-title-${Math.random().toString(16).slice(2)}`,
    []
  );

  useEffect(() => {
    if (!open) return;

    lastActiveRef.current = document.activeElement as HTMLElement;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key !== 'Tab') return;
      const root = dialogRef.current;
      if (!root) return;
      const focusables = getFocusableElements(root);
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement as HTMLElement | null;

      if (e.shiftKey) {
        if (!active || active === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (!active || active === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    window.addEventListener('keydown', onKeyDown);

    window.setTimeout(() => {
      const root = dialogRef.current;
      if (!root) return;
      const focusables = getFocusableElements(root);
      (focusables[0] || root).focus();
    }, 0);

    return () => {
      window.removeEventListener('keydown', onKeyDown);
      try {
        lastActiveRef.current?.focus();
      } catch {
        // ignore
      }
    };
  }, [open, onClose]);

  if (!open) return null;

  const toggleCategory = (key: CategoryRow['key'], value: boolean) => {
    if (key === 'necessary') return;
    setState((prev) => ({ ...prev, [key]: value }));
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <button
        type="button"
        className="absolute inset-0 bg-foreground/40 backdrop-blur-[1px]"
        aria-label="Chiudi preferenze cookie"
        onClick={onClose}
      />
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className="relative w-full max-w-2xl rounded-2xl border border-border bg-card shadow-elev2 overflow-hidden max-h-[90vh] flex flex-col"
      >
        <div className="p-5 border-b border-border flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div id={titleId} className="text-base font-semibold text-foreground">
              Preferenze cookie
            </div>
            <div className="text-sm text-muted-foreground mt-1">
              Puoi modificare il consenso in qualsiasi momento. I cookie necessari restano sempre
              attivi.
            </div>
          </div>
          <button
            type="button"
            aria-label="Chiudi"
            onClick={onClose}
            className="shrink-0 p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-3 overflow-y-auto">
          {CATEGORIES.map((cat) => {
            const Icon = cat.icon;
            const checked = cat.locked ? true : Boolean(state[cat.key]);
            const inputId = `cookie-cat-${cat.key}`;
            return (
              <label
                key={cat.key}
                htmlFor={inputId}
                className={`flex items-start justify-between gap-4 rounded-xl border border-border bg-muted/20 p-4 transition-colors ${
                  cat.locked ? 'opacity-90' : 'hover:border-foreground/20 cursor-pointer'
                }`}
              >
                <div className="flex items-start gap-3 min-w-0">
                  <div
                    className={`shrink-0 p-2 rounded-lg border ${
                      cat.locked
                        ? 'bg-muted text-muted-foreground border-border'
                        : 'bg-brand/10 text-brand border-brand/20'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-foreground flex items-center gap-2">
                      {cat.title}
                      {cat.locked ? (
                        <span className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">
                          Sempre attivi
                        </span>
                      ) : null}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1 leading-relaxed">
                      {cat.description}
                    </div>
                  </div>
                </div>
                <Switch
                  id={inputId}
                  checked={checked}
                  disabled={cat.locked}
                  onCheckedChange={(v: boolean) => toggleCategory(cat.key, Boolean(v))}
                  aria-label={`Consenso cookie ${cat.title}`}
                />
              </label>
            );
          })}
        </div>

        <div className="p-4 border-t border-border flex flex-col sm:flex-row gap-2 sm:items-center sm:justify-between">
          <div className="flex flex-wrap gap-2">
            {onRejectAll ? (
              <Button type="button" variant="outline" size="sm" onClick={onRejectAll}>
                Rifiuta tutti
              </Button>
            ) : null}
            {onAcceptAll ? (
              <Button type="button" variant="outline" size="sm" onClick={onAcceptAll}>
                Accetta tutti
              </Button>
            ) : null}
          </div>
          <div className="flex gap-2 justify-end">
            <Button type="button" variant="ghost" size="sm" onClick={onClose}>
              Annulla
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={() => onSave({ ...state, necessary: true })}
            >
              Salva preferenze
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
